import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitMerge } from "lucide-react";
import { StatusBadge, PriorityBadge } from "@/components/help/SupportBadges";
import { api } from "@/lib/api";

const sel = "rounded-md border border-line bg-bg px-2.5 py-1.5 text-xs text-ink-primary focus:outline-none";
const REQ_CLS = {
  new: "text-cyan-300 bg-cyan-500/15",
  under_review: "text-amber-300 bg-amber-500/15",
  planned: "text-violet-300 bg-violet-500/15",
  completed: "text-emerald-300 bg-emerald-500/15",
  declined: "text-rose-300 bg-rose-500/15",
  merged: "text-ink-muted bg-bg-elevated",
};

export default function AdminRequestsTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [merging, setMerging] = useState(null); // reference being merged
  const [mergeTarget, setMergeTarget] = useState("");

  const load = (f = filter) =>
    api.get(`/admin/helpdesk/requests${f ? `?request_status=${f}` : ""}`)
      .then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const patch = async (reference, body) => {
    try {
      await api.patch(`/admin/helpdesk/requests/${reference}`, body);
      toast.success("Request updated");
      setMerging(null); setMergeTarget("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-requests-tab">
      <div className="flex items-center justify-between gap-2">
        <select className={sel} value={filter} data-testid="requests-filter"
          onChange={(e) => { setFilter(e.target.value); load(e.target.value); }}>
          <option value="">All requests</option>
          {(data?.statuses || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <span className="text-[10px] text-ink-muted">Submitted features are never publicly promised — statuses are internal until you announce them.</span>
      </div>
      <div className="space-y-2">
        {(data?.requests || []).map((r) => (
          <div key={r.reference} className="rounded-xl border border-line bg-bg-surface p-4"
            data-testid={`request-row-${r.reference}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] text-brand-amber">{r.reference}</span>
              <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded ${REQ_CLS[r.request_status || "new"]}`}
                data-testid={`request-status-${r.reference}`}>
                {(r.request_status || "new").replace(/_/g, " ")}
              </span>
              <PriorityBadge priority={r.priority} />
              <span className="ml-auto font-mono text-[9px] text-ink-muted">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
            <div className="text-sm text-ink-primary mt-1.5">{r.subject.replace(/^Feature request: /, "")}</div>
            <div className="text-[11px] text-ink-muted">{r.name || r.email}
              {r.feature_details?.related_feature && ` · Related: ${r.feature_details.related_feature}`}
              {r.merged_into && ` · Merged into ${r.merged_into}`}
            </div>
            {r.feature_details?.problem && (
              <div className="text-[11px] text-ink-secondary mt-1.5">Solves: {r.feature_details.problem}</div>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <select className={sel} value={r.request_status || "new"} data-testid={`request-status-select-${r.reference}`}
                onChange={(e) => patch(r.reference, { request_status: e.target.value })}>
                {(data?.statuses || []).filter((s) => s !== "merged").map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              {merging === r.reference ? (
                <>
                  <input className={`${sel} w-40`} value={mergeTarget} placeholder="BF-2026-000123"
                    data-testid="merge-target-input" onChange={(e) => setMergeTarget(e.target.value)} />
                  <button type="button" onClick={() => patch(r.reference, { merge_into: mergeTarget.trim() })}
                    data-testid="merge-confirm-btn"
                    className="rounded-md bg-brand-amber px-3 py-1.5 text-[11px] font-semibold text-black">Merge</button>
                  <button type="button" onClick={() => setMerging(null)} className="text-[11px] text-ink-muted">Cancel</button>
                </>
              ) : (
                r.request_status !== "merged" && (
                  <button type="button" onClick={() => setMerging(r.reference)} data-testid={`merge-btn-${r.reference}`}
                    className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary transition">
                    <GitMerge className="h-3 w-3" /> Merge duplicate
                  </button>
                )
              )}
            </div>
          </div>
        ))}
        {data && data.requests.length === 0 && <div className="text-sm text-ink-muted py-6 text-center">No feature requests yet.</div>}
      </div>
    </div>
  );
}
